<template>
  <section class="checkout">
    <div class="inner">
      <paragraph-header size="2">{{ $t('yourPersonalData') }}</paragraph-header>
      <div class="columns">
        <section class="column var_25">
          <div class="inner var_background" :class="{ is_disabled: fetchingDataFromSwissPass }">
            <span class="label">{{ $t('getYourDataFromSwisspass') }}</span>
            <div class="field var_100">
              <masked-input
                v-model="swisspassID"
                :class="{ is_filled: swisspassID }"
                class="masked"
                id="swisspassid"
                mask="S11-111-111-111"
                type="text"
                :disabled="fetchingDataFromSwissPass"
              />
              <label for="swisspassid">{{ $t('swisspassID') }}</label>
            </div>
            <div class="field var_narrow">
              <input
                id="zipCode"
                type="password"
                :class="{ is_filled: zipCode }"
                v-model="zipCode"
                :disabled="fetchingDataFromSwissPass"
              />
              <label for="zipCode">{{ $t('zipCode') }}</label>
            </div>
            <img
              src="/app/themes/leihbox/dist/images/card.png"
              width="97px"
              height="62px"
              style="margin-left: 25px; margin-right: auto"
              alt=""
            />
            <div>
              <app-button @click="getDataFromSwissPass" v-if="!swissPassCustomerID">{{ $t('Load data') }}</app-button>
              <app-button @click="clearSwissPassData" v-if="swissPassCustomerID">
                <template v-slot:icon-prev>
                  <svg class="prev" width="14" height="14" viewbox="0 0 14 14">
                    <use xlink:href="#close"></use>
                  </svg>
                </template>
                {{ $t('Remove data') }}
                <template v-slot:icon-next>
                  <svg class="next" width="14" height="14" viewbox="0 0 14 14">
                    <use xlink:href="#close"></use>
                  </svg>
                </template>
              </app-button>

              <span class="error" v-show="swissPassIdError">{{ swissPassIdError }}</span>
            </div>
            <div class="small_overlay">
              <the-loader :isAbsolute="true"></the-loader>
            </div>
          </div>
          <div class="inner var_top_margin var_background" :class="{ is_disabled: validatingVoucher }">
            <span class="label">{{ $t('Do you have voucher?') }}</span>
            <div class="field var_100">
              <input
                id="voucherCode"
                type="text"
                :class="{ is_filled: voucherCode }"
                v-model="voucherCode"
                :disabled="validatingVoucher || voucherApplied"
              />
              <label for="voucherCode">{{ $t('Voucher Code') }}</label>
            </div>
            <app-button @click="validateVoucher">{{ $t('Apply Voucher') }}</app-button>

            <span class="error" v-show="voucherIsError">{{ voucherIsError }}</span>

            <div class="small_overlay">
              <the-loader :isAbsolute="true"></the-loader>
            </div>
          </div>
        </section>
        <section class="column var_75">
          <div class="inner">
            <div class="fields_group" :class="group.class" v-for="group in fieldsGroup" :key="group.label">
              <paragraph-header size="3" v-if="group.label">{{ $t(group.label) }}</paragraph-header>
              <template v-for="field in group.fields">
                <paragraph-header size="3" v-if="field.header" :key="field.key">{{
                  $t(field.header)
                }}</paragraph-header>
                <div class="field" :class="field.class" :key="`field_${field.key}`">
                  <input
                    v-if="['text', 'email', 'phone'].indexOf(field.type) !== -1"
                    :type="field.type"
                    v-model="_self[field.key]"
                    :class="{ is_filled: _self[field.key] }"
                    :id="field.id"
                    :required="field.required"
                    :name="field.name"
                    :disabled="field.disabled && _self[field.disabled]"
                  />

                  <model-select
                    :id="field.id"
                    :name="field.name"
                    :class="{ is_filled: _self[field.key] }"
                    v-if="['select'].indexOf(field.type) !== -1"
                    :options="field.options"
                    v-model="_self[field.key]"
                    :isDisabled="field.disabled && _self[field.disabled] !== null"
                  >
                  </model-select>

                  <textarea
                    v-if="field.type === 'textarea'"
                    v-model="_self[field.key]"
                    :class="{ is_filled: _self[field.key] }"
                    :id="field.id"
                    :required="field.required"
                    :name="field.name"
                    @input="onTextAreaChange"
                  ></textarea>
                  <label :for="field.id">{{ $t(field.label) }}</label>
                  <span class="note" v-if="field.note">{{ $t(field.note) }}</span>
                </div>
              </template>
            </div>
            <div class="field">
              <input type="checkbox" id="terms-of-service" class="styled" v-model="termsOfService" />
              <label for="terms-of-service">
                <span class="checkbox_presentation">
                  <svg width="12" height="12" viewbox="0 0 24 24">
                    <use xlink:href="#tick"></use>
                  </svg>
                </span>
                <i18n path="I accept the" class="subtext" tag="div">
                  <a href="/terms-and-conditions" target="_blank">{{ $t('Terms and Conditions') }}</a>
                </i18n>
              </label>
            </div>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
/*global language*/
import { ModelSelect } from 'vue-search-select'
import countryList from '../../assets/countries'
import { SwissPassService } from '@/api'
import TheLoader from './TheLoader.vue'
import ParagraphHeader from '../atoms/ParagraphHeader.vue'
import { mapState } from 'vuex'
import AppButton from '../atoms/AppButton.vue'
import i18n from '../../lang'
import MaskedInput from 'vue-masked-input'

const fieldsGroup = [
  {
    class: ['var_wide'],
    fields: [
      {
        type: 'select',
        id: 'salutation',
        key: 'salutation',
        label: 'Salutation',
        required: false,
        class: ['var_10'],
        options: [
          {
            value: 'HERR',
            text: 'Herr',
          },
          {
            value: 'FRAU',
            text: 'Frau',
          },
        ],
        disabled: 'swissPassCardID',
      },
      {
        type: 'text',
        id: 'first_name',
        key: 'firstName',
        label: 'First Name',
        required: true,
        class: ['var_38'],
        disabled: 'swissPassCardID',
      },
      {
        type: 'text',
        id: 'last_name',
        key: 'lastName',
        label: 'Last Name',
        required: true,
        class: ['var_38'],
        disabled: 'swissPassCardID',
      },
    ],
  },
  {
    label: 'billingAddress',
    fields: [
      {
        type: 'text',
        id: 'company',
        key: 'company',
        label: 'Company',
        required: false,
        note: 'fillInIfBusinessOrder',
        class: ['var_100'],
      },
      {
        type: 'text',
        id: 'address',
        key: 'address',
        label: 'Address',
        required: true,
        class: ['var_100'],
        disabled: 'swissPassCardID',
      },
      {
        type: 'text',
        id: 'postCode',
        key: 'postCode',
        label: 'zipCode',
        required: true,
        class: ['var_25'],
        disabled: 'swissPassCardID',
      },
      {
        type: 'text',
        id: 'city',
        key: 'city',
        label: 'City',
        required: true,
        class: ['var_75'],
        disabled: 'swissPassCardID',
      },

      {
        type: 'select',
        id: 'country',
        key: 'country',
        label: 'Country',
        required: true,
        options: countryList[language.current],
        class: ['var_66'],
        disabled: 'swissPassCardID',
      },
    ],
  },
  {
    label: 'Contact Information',
    fields: [
      {
        type: 'email',
        id: 'email_address',
        key: 'emailAddress',
        label: 'Email address',
        required: true,
        class: ['var_100'],
        disabled: 'swissPassCardID',
      },
      {
        type: 'phone',
        id: 'mobile',
        key: 'mobile',
        label: 'Mobile',
        required: true,
        class: ['var_100'],
      },
      {
        header: 'Additional Information',
        type: 'textarea',
        id: 'notes',
        key: 'notes',
        label: 'Notes',
        required: false,
        class: ['var_100'],
      },
    ],
  },
]

const dynamicComputedProperty = () => {
  const result = {}
  const fields = fieldsGroup.reduce((sum, current) => {
    return [...sum, ...current.fields]
  }, [])

  fields.forEach((field) => {
    const commitNameKey = field.key.charAt(0).toUpperCase() + field.key.slice(1)

    result[field.key] = {
      set(value) {
        this.$store.commit(`user/set${commitNameKey}`, value)
      },
      get() {
        return this.$store.state.user[field.key]
      },
    }
  })

  return result
}

export default {
  metaInfo: {
    title: i18n.t('yourPersonalData'),
  },
  data() {
    return {
      swisspassID: null,
      zipCode: null,
      swissPassIdError: null,
      fieldsGroup: fieldsGroup,
      fetchingDataFromSwissPass: false,
      validatingVoucher: false,
      voucherApplied: false,
      voucherIsError: false,
    }
  },
  components: {
    ParagraphHeader,
    ModelSelect,
    AppButton,
    TheLoader,
    MaskedInput,
  },
  created() {
    this.createBasket()
  },
  computed: {
    ...dynamicComputedProperty(),
    ...mapState('user', ['swissPassCardID', 'swissPassCustomerID']),
    termsOfService: {
      set(value) {
        this.$store.commit('user/setAcceptTermsOfService', value)
      },
      get() {
        return this.$store.state.user.acceptTermsOfService
      },
    },
    voucherCode: {
      set(value) {
        this.$store.commit('cart/setVoucherCode', value)
        this.voucherIsError = false
      },
      get() {
        return this.$store.state.cart.voucherCode
      },
    },
  },
  methods: {
    async createBasket() {
      this.$store.dispatch('cart/createRemoteCart')
    },
    clearSwissPassData() {
      this.$store.commit('user/setSalutation', null)
      this.$store.commit('user/setFirstName', null)
      this.$store.commit('user/setLastName', null)
      this.$store.commit('user/setEmailAddress', null)
      this.$store.commit('user/setCity', null)
      this.$store.commit('user/setPostCode', null)
      this.$store.commit('user/setAddress', null)
      this.$store.commit('user/setCountry', null)
      this.$store.commit('user/setSwissPassCardID', null)
      this.$store.commit('user/setSwissPassCustomerID', null)
      this.$store.commit('user/setSwissPassLogin', 'No')
      this.swisspassID = null
      this.zipCode = null
    },

    async getDataFromSwissPass() {
      this.fetchingDataFromSwissPass = true
      const params = {
        aid: this.swisspassID,
        plz: this.zipCode,
      }

      try {
        const resp = await SwissPassService.getUserData(params)
        this.swissPassIdError = null
        if (resp.return) {
          const { vorname, name, email, anrede } = resp.return.kundeUndKarteAntwort.kundenDatenAntwort
          const { ort, postleitzahl, strasseUndHausNummer } =
            resp.return.kundeUndKarteAntwort.kundenDatenAntwort.kmwNutzerAdresse
          const country = resp.return.kundeUndKarteAntwort.kundenDatenAntwort.kmwNutzerAdresse.land.isoCode
          const customerID = resp.return.kundeUndKarteAntwort.kundenNummer.kundenID
          const cardID = resp.return.kundeUndKarteAntwort.karteDatenAntwort.ausweisID
          this.$store.commit('user/setSalutation', anrede)
          this.$store.commit('user/setFirstName', vorname)
          this.$store.commit('user/setLastName', name)
          this.$store.commit('user/setEmailAddress', email)
          this.$store.commit('user/setCity', ort)
          this.$store.commit('user/setPostCode', postleitzahl)
          this.$store.commit('user/setAddress', strasseUndHausNummer)
          this.$store.commit('user/setCountry', country)
          this.$store.commit('user/setSwissPassCardID', cardID)
          this.$store.commit('user/setSwissPassCustomerID', customerID)
          this.$store.commit('user/setSwissPassLogin', 'Yes')

          this.fetchingDataFromSwissPass = false
        }
      } catch (err) {
        this.fetchingDataFromSwissPass = false
        this.swissPassIdError = this.$t('swissPassError')
      }
    },

    async validateVoucher() {
      this.validatingVoucher = true
      this.voucherIsError = false

      try {
        const resp = await this.$store.dispatch('cart/validateVoucherCode')
        if (resp.status) {
          this.voucherApplied = true
        } else {
          this.voucherIsError = resp.message
        }
        this.validatingVoucher = false
      } catch (err) {
        this.validatingVoucher = false
        this.voucherIsError = 'Something goes wrong, please try later.'
      }
    },

    onTextAreaChange(ev) {
      const element = ev.target
      element.style.height = '5px'
      element.style.height = element.scrollHeight + 'px'
    },
  },
}
</script>

<style lang="scss" scoped>
section.checkout {
  width: 100%;

  & > .inner {
    @include container();
  }
}

.columns {
  display: flex;
  justify-content: flex-start;
  margin-top: 25px;
  width: 100%;
  flex-wrap: wrap;

  @include mq($from: desktop) {
    justify-content: space-between;
  }

  .column {
    position: relative;

    &.var_25 {
      flex: 1 0 100%;

      @include mq($from: tablet) {
        flex: 1 0 280px;
        max-width: 280px;
      }
    }

    &.var_75 {
      flex: 1 0 calc(100% + 25px);
      margin-top: 50px;

      @include mq($from: tablet) {
        flex: 1 0 calc(94% - 280px);
        margin-left: 6%;
        margin-top: 0;
        max-width: 445px;
      }

      @include mq($from: desktop) {
        max-width: none;
      }
    }

    .inner {
      position: relative;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: flex-start;

      &.var_top_margin {
        margin-top: 30px;
      }

      &.var_background {
        background: $colorBeigeDark;
        border-radius: 10px;
        padding: 20px 30px;

        .field {
          margin-right: 0;
        }
      }

      > .label {
        @include fontStyleCopyRegular;
        margin-bottom: 20px;
      }

      &.is_disabled {
        .small_overlay {
          opacity: 1;
          visibility: visible;
          transition: opacity 0.3s, visibility 0s ease 0s;
        }
      }
    }
  }

  .field {
    position: relative;
    margin-bottom: 25px;
    margin-right: 25px;

    &.var_wide {
      width: 75%;
    }

    &.var_narrow {
      flex: 1 0 70px;
    }

    &.var_10 {
      flex: 1 0 calc(10% - 25px);
      min-width: 108px;
    }

    &.var_25 {
      flex: 1 0 70px;
    }

    &.var_38 {
      flex: 1 0 calc(38% - 25px);
      min-width: 200px;
    }

    &.var_66 {
      width: 66%;
    }

    &.var_75 {
      flex: 1 0 240px;
    }

    &.var_49 {
      width: 49%;
    }

    &.var_100 {
      width: 100%;
    }

    .note {
      @include fontStyleCopyXs;
      display: block;
      padding: 5px 0;
      width: 100%;
    }

    textarea {
      min-height: 110px;
    }
  }

  .small_overlay {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s, visibility 0s ease 0.3s;
  }
}

.error {
  font-size: 14px;
  color: red;
  display: block;
  padding: 10px 0;
}

.fields_group {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  @include mq($from: desktop) {
    width: 48%;
  }

  &.var_wide {
    width: 100%;
  }

  h3 {
    @include fontStyleFooterHeader;
    flex: 0 0 100%;
    margin: 25px 0 10px;
  }
}
</style>
